import {useRef, useState, useEffect} from "react";
import Container from "@components/layout/Container";
import {useSanityQuery} from "@/hooks/useSanity";

const QUERY = `*[_type == "testimonial"] | order(date desc) { _id, name, city, text, rating }`;

export default function Testimonials() {
	const {data} = useSanityQuery(QUERY);
	const trackRef = useRef(null);
	const [canPrev, setCanPrev] = useState(false);
	const [canNext, setCanNext] = useState(false);

	const sliderForDesktop = (data?.length ?? 0) > 4;

	useEffect(() => {
		const el = trackRef.current;
		if (!el) return;

		const update = () => {
			const max = el.scrollWidth - el.clientWidth - 1;
			setCanPrev(el.scrollLeft > 1);
			setCanNext(el.scrollLeft < max);
		};
		update();
		el.addEventListener("scroll", update, {passive: true});
		window.addEventListener("resize", update);
		return () => {
			el.removeEventListener("scroll", update);
			window.removeEventListener("resize", update);
		};
	}, [data]);

	const step = (dir) => {
		const el = trackRef.current;
		if (!el) return;
		const card = el.querySelector(".t-card");
		const gap = parseFloat(getComputedStyle(el).gap) || 16;
		const distance = card ? card.offsetWidth + gap : el.clientWidth;
		el.scrollBy({left: dir * distance, behavior: "smooth"});
	};

	if (!data?.length) return null;

	return (
		<section className="testimonials">
			<Container>
				<header className="testimonials-header">
					<span className="eyebrow-centered eyebrow-centered--on-light">
						Відгуки клієнтів
					</span>
					<h2 className="h2-serif">
						Нам довіряють <span style={{color: "rgba(0, 71, 160, 1)", fontStyle: "italic"}}>тисячі</span> родин
					</h2>
				</header>

				<div className={`testimonials-track-wrap${sliderForDesktop ? " is-slider" : ""}`}>
					<div
						className={`testimonials-grid${sliderForDesktop ? " is-slider" : ""}`}
						ref={trackRef}
					>
						{data.map((r) => (
							<article key={r._id} className="t-card">
								<div className="t-stars">{"★".repeat(r.rating ?? 5)}</div>
								<p className="t-text">{r.text}</p>
								<div className="t-person">
									<span className="t-avatar">{r.name?.[0] ?? "?"}</span>
									<div className="t-person-meta">
										<span className="t-name">{r.name}</span>
										<span className="t-city">{r.city}</span>
									</div>
								</div>
							</article>
						))}
					</div>

					{sliderForDesktop && (
						<div className="testimonials-controls">
							<button
								type="button"
								className="testimonials-arrow"
								aria-label="Попередній відгук"
								onClick={() => step(-1)}
								disabled={!canPrev}
							>
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
									<path d="M15 18l-6-6 6-6" />
								</svg>
							</button>
							<button
								type="button"
								className="testimonials-arrow"
								aria-label="Наступний відгук"
								onClick={() => step(1)}
								disabled={!canNext}
							>
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
									<path d="M9 6l6 6-6 6" />
								</svg>
							</button>
						</div>
					)}
				</div>
			</Container>
		</section>
	);
}
